<script>
import {
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
  iDrawer,
  iIcon,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconType,
  iKeyIconStyle,
  iSpinner,
  showToast,
} from '@inscribe/signal';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import {
  CHECK_INFO,
  CHECKS_README_URL,
  COMPLIANCE_RESULT_TO_STATUS,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
  COMPLIANCE_CHECK_TYPE_TO_ICON,
} from '@/constants/businessComplianceAgent';
import UrlsDisplayer from '@/V2/components/UrlsDisplayer.vue';
import InfoIcon from '@/V2/components/InfoIcon.vue';
import InformationCardCheckItem from '@/V2/customers/business-compliance-agent/InformationCardCheckItem.vue';
import { mapGetters, mapActions } from 'vuex';
import DocumentCard from '@/V2/components/documents/DocumentCard.vue';
import {
  mapInsightResultToStatus,
  mapInsightResultToReadableLabel,
} from '@/V2/customers/overview/insight.utils';

export default {
  name: 'CustomerComplianceCheckDetails',
  components: {
    iDrawer,
    StatusBar,
    iKeyIcon,
    UrlsDisplayer,
    InfoIcon,
    InformationCardCheckItem,
    DocumentCard,
    iIcon,
    iSpinner,
    iButton,
  },
  props: {
    check: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      COMPLIANCE_RESULT_TO_STATUS,
      COMPLIANCE_RESULT_TO_READABLE_LABEL,
      COMPLIANCE_CHECK_TYPE_TO_ICON,
      iKeyIconShape,
      iKeyIconType,
      iKeyIconStyle,
      selectedSubCheckType: null,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters(['getCachedFirstPageOfSummaryDocumentsForCustomerUUID']),
    checkConfig() {
      return (
        CHECK_INFO['COMPANY'][this.check.type] ||
        CHECK_INFO['INDIVIDUAL'][this.check.type] || {
          description:
            'View documentation about this compliance check and its requirements.',
          url: CHECKS_README_URL,
        }
      );
    },
    relatedDocuments() {
      const allDocs =
        this.getCachedFirstPageOfSummaryDocumentsForCustomerUUID(
          this.$route.params.customer_id,
        ) ?? [];
      const relatedDocIds = this.check.related_inscribe_document_ids ?? [];
      // Support both string IDs (new standard) and integer IDs (old runs before the change)
      return allDocs.filter((doc) =>
        relatedDocIds.map(String).includes(String(doc.id)),
      );
    },
    hasRelatedDocuments() {
      return (this.check.related_inscribe_document_ids ?? []).length > 0;
    },
    isLoadingDocuments() {
      return (
        this.hasRelatedDocuments &&
        !this.getCachedFirstPageOfSummaryDocumentsForCustomerUUID(
          this.$route.params.customer_id,
        )
      );
    },
    returnToPath() {
      return this.$route.fullPath;
    },
  },
  mounted() {
    this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
      this.$route.params.customer_id,
    );
  },
  methods: {
    ...mapActions(['fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID']),
    onHideDrawer() {
      this.$emit('close');
    },
    onSubCheckClick(subCheck) {
      this.selectedSubCheckType =
        this.selectedSubCheckType === subCheck.type ? null : subCheck.type;
    },
    copyToClipboard(value) {
      navigator.clipboard.writeText(value);
      showToast({
        title: 'Copied',
        text: `Value successfully copied`,
      });
    },
    viewDocument(document) {
      this.$router.push({
        name: 'document-signals',
        params: {
          customer_id: document.customer_id,
          document_id: document.id,
        },
        query: {
          return_to: this.returnToPath,
        },
      });
    },
    getCheckSubInsights(check) {
      // sub_checks as been replaced with sub_insights with the unified agent. Remove sub_checks once the flag is removed
      return check.sub_checks ?? check.sub_insights ?? [];
    },
    getCheckState(check) {
      return (
        COMPLIANCE_RESULT_TO_STATUS[check.result] ||
        mapInsightResultToStatus(check.result)
      );
    },
    getCheckReadableLabel(check) {
      return (
        COMPLIANCE_RESULT_TO_READABLE_LABEL[check.result] ||
        mapInsightResultToReadableLabel(check.result)
      );
    },
  },
};
</script>

<template>
  <i-drawer
    :visible="!!check"
    @hide-drawer="onHideDrawer"
    :title="check?.title"
    class="customer-compliance-check-details"
    data-testid="customer-compliance-check-details"
  >
    <div v-if="check" class="container d-flex flex-column i-gap-4">
      <!-- status bar-->
      <status-bar
        v-if="check.result"
        :state="getCheckState(check)"
        :label="getCheckReadableLabel(check)"
        data-testid="customer-compliance-check-status-bar"
      />
      <!-- analysis -->
      <div
        v-if="check.rationale || check.value"
        class="panel-rationale d-flex flex-column i-gap-9 i-py-6 i-radius-xl"
      >
        <div
          v-if="check.rationale"
          class="i-text-subheading-2 i-text-neutral-120"
        >
          Analysis
        </div>
        <div class="i-text-body-2 i-text-neutral-120">
          {{ check.rationale || check.value }}
          <urls-displayer :urls="check.urls" class="i-mt-4" />
        </div>
        <i-button
          label="Copy"
          class="copy-button"
          left-icon="medium-copy-1"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.SOLID_LIGHT"
          :size="iButtonSizes.SMALL"
          @button-click="copyToClipboard(check.rationale || check.value)"
        />
      </div>

      <div
        v-if="check.source"
        class="source-container d-flex align-items-center i-gap-4 i-radius-lg i-px-6 i-py-5"
      >
        <i-icon
          symbol="small-solid-info"
          :size="12"
          class="i-text-neutral-100"
        />
        <div class="i-text-body-3 i-text-neutral-110">Source</div>
        <div class="i-text-subheading-3 i-text-neutral-120">
          {{ check.source }}
        </div>
      </div>

      <!-- documents list -->
      <div
        v-if="isLoadingDocuments"
        class="related-documents d-flex align-items-center justify-content-center i-p-6 i-radius-xl"
      >
        <iSpinner size="sm" />
      </div>

      <div
        v-else-if="relatedDocuments.length > 0"
        class="related-documents document-cards-list i-radius-xl"
      >
        <document-card
          v-for="docItem in relatedDocuments"
          :key="docItem.id"
          :document="docItem"
          size="small"
          :show-checks="false"
          :show-tags="false"
          :return-to="returnToPath"
          @on-right-icon-click="viewDocument(docItem)"
          @on-document-click="viewDocument(docItem)"
        />
      </div>

      <!-- Sub-checks section -->
      <template v-if="getCheckSubInsights(check)?.length > 0">
        <div class="d-flex flex-column i-gap-4">
          <div class="i-text-subheading-2 i-text-neutral-120">Sub-Checks</div>
          <ul
            class="compliance-items d-flex flex-column i-mb-0 i-radius-xl w-100"
          >
            <li
              v-for="subCheck in getCheckSubInsights(check)"
              :key="subCheck.type"
              class="sub-check-item"
              data-testid="customer-compliance-check-sub-check-item"
            >
              <information-card-check-item
                :check="subCheck"
                :on-click="onSubCheckClick"
              />

              <!-- analysis -->
              <div
                v-if="selectedSubCheckType === subCheck.type"
                class="panel-rationale d-flex flex-column i-gap-4 i-p-6 i-radius-xl"
              >
                <status-bar
                  v-if="subCheck.result"
                  :state="getCheckState(subCheck)"
                  :label="getCheckReadableLabel(subCheck)"
                />
                <div class="i-text-body-3 i-text-neutral-110">
                  {{ subCheck.rationale }}
                  <urls-displayer :urls="subCheck.urls" class="i-mt-4" />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </i-drawer>
</template>

<style lang="scss" scoped>
.customer-compliance-check-details {
  .copy-button {
    width: 80px;
  }

  .source-container {
    border: 1px solid $light-neutral-60;
  }

  ul.compliance-items {
    list-style-type: none;
    padding: 0;
    border: 1px solid $light-neutral-60;

    li.sub-check-item {
      border-bottom: 1px solid $light-neutral-60;

      &:last-child {
        border-bottom: none;
      }
    }
  }
}
</style>
